import {UIGameMapRoadView} from './UIGameMapRoadView.ts'
import {UIGameMapShipView} from './UIGameMapShipView.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import {type UIGameMechanic} from '../UIGameEventController.ts'
import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {Sound} from '../../Sound.ts'
import {ArrayUtils} from '../../../../../shared/utils/ArrayUtils.ts'
import {TileEdgeType} from '@colonist/game-states'
import {type TileEdge} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import SoundType = Sound.SoundType
import {type ToolTipText} from '../ui_game_tooltip/UIGameToolTipController.ts'

export class UIGameMapEdgeController implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager

    private readonly roadViews: UIGameMapRoadView[] = []
    private readonly shipViews: UIGameMapShipView[] = []

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        const drawingExistingEdges = true
        this.updateEdges(drawingExistingEdges)
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.MapStateUpdated: this.updateEdges(data as boolean == false); break
            default: break
        }
    }

    private updateEdges(drawingExistingEdges: boolean = false) {
        this.updateMovedShips()
        this.updateRemovedRoads()
        for(const edge of this.uiGameManager.gameController.mapValidator.tileState.tileEdges) {
            if(edge.type == undefined) continue
            if(this.edgeExists(edge)) continue
            if(this.shipEdgeExists(edge)) continue
            this.drawEdge(edge, drawingExistingEdges)
        }
    }

    static playEdgeSound(edge: TileEdge) {
        switch(edge.type) {
            case TileEdgeType.Road: return Sound.playSound(SoundType.RoadPlace)
            case TileEdgeType.Ship: return Sound.playSound(SoundType.ShipPlace)
            default: return
        }
    }

    private edgeExists(edge: TileEdge): boolean {
        for(const roadView of this.roadViews) {
            if(roadView.edge.sameEdge(edge)) return true
        }
        return false
    }

    private shipEdgeExists(edge: TileEdge): boolean {
        for(const shipView of this.shipViews) {
            if(shipView.edge.sameEdge(edge)) return true
        }
        return false
    }

    private getExistingViewAtEdge(edge: TileEdge): UIGameMapRoadView | UIGameMapShipView | undefined {
        for(const roadView of this.roadViews) {
            if(roadView.edge.sameEdge(edge)) return roadView
        }

        for(const shipView of this.shipViews) {
            if(shipView.edge.sameEdge(edge)) return shipView
        }

        return undefined
    }

    private updateRemovedRoads() {
        const roads = [...this.roadViews]
        for(const roadView of roads) {
            const wasRoadRemoved = roadView.edge.type == undefined
            if(!wasRoadRemoved) continue

            roadView.removeView()
            ArrayUtils.removeElementFromArray(this.roadViews, roadView)
        }
    }

    private updateMovedShips() {
        const ships = [...this.shipViews]
        for(const shipView of ships) {
            const wasShipMoved = shipView.edge.type == undefined
            if(!wasShipMoved) continue

            shipView.removeView()
            ArrayUtils.removeElementFromArray(this.shipViews, shipView)
        }
    }

    private drawEdge(edge: TileEdge, drawingExistingEdges: boolean) {
        const pointerover = () => {
            this.highlightLongestRoadIncludingEdge(edge)
        }

        const pointerout = () => {
            this.removeLongestRoadHighlight()
            this.resetEdgeViewLayer()
        }

        const toolTipText: ToolTipText = () => {
            const longestRouteTouchingTileEdges = this.uiGameManager.gameController.mapValidator.getLongestRouteTouchingTileEdge(edge)
            return {title: {key: 'strings:colonistCommon.utils.emptyString'}, body: [{key: 'strings:game.tooltips.roadLength', options: {count: longestRouteTouchingTileEdges.length}}]}
        }
        if(edge.type == TileEdgeType.Road) {
            const roadView = new UIGameMapRoadView(this.uiGameManager, edge, this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight, this.uiGameManager.mapController.mapView, drawingExistingEdges)
            this.roadViews.push(roadView)
            roadView.executeStartAnimation(edge, drawingExistingEdges, pointerover, pointerout, toolTipText)
            this.uiGameManager.mapController.mapView.sortChildren()
        }
        if(edge.type == TileEdgeType.Ship) {
            const shipView = new UIGameMapShipView(this.uiGameManager, edge, this.uiGameManager.mapController.mapView, drawingExistingEdges)
            this.shipViews.push(shipView)
            shipView.executeStartAnimation(edge, drawingExistingEdges, pointerover, pointerout, toolTipText)
            this.uiGameManager.mapController.mapView.sortChildren()
        }
    }

    private highlightLongestRoadIncludingEdge(tileEdge: TileEdge) {
        const longestRouteTouchingTileEdges = this.uiGameManager.gameController.mapValidator.getLongestRouteTouchingTileEdge(tileEdge)

        const layerOffsetToBringToFront = 0.1
        for(const edge of longestRouteTouchingTileEdges) {
            const edgeView = this.getExistingViewAtEdge(edge)
            if(edgeView == undefined) continue

            edgeView.activateHighlight()
            edgeView.setLayer(layerOffsetToBringToFront)
        }
        this.uiGameManager.mapController.mapView.sortChildren()
    }

    private resetEdgeViewLayer() {
        for(const roadView of this.roadViews) {
            roadView.setLayer()
        }
        for(const shipView of this.shipViews) {
            shipView.setLayer()
        }
        this.uiGameManager.mapController.mapView.sortChildren()
    }

    private removeLongestRoadHighlight() {
        for(const roadView of this.roadViews) {
            roadView.deactivateHighlight()
        }
        for(const shipView of this.shipViews) {
            shipView.deactivateHighlight()
        }
    }
}
